import {
  Component,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  TemplateRef,
} from "@angular/core";
import {
  MatRowDef,
  MatTableDataSource,
  MatTableModule,
} from "@angular/material/table";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatIcon, MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSidenav, MatSidenavContainer } from "@angular/material/sidenav";
import { MatMenuModule } from "@angular/material/menu";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { QuotaAttributes } from "@albatrosdeveloper/ave-models-package";
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { Subscription } from "rxjs";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { ColDef, GridConfig, GridReadyEvent, SmartGridComponent, SortChangedEvent } from "app/shared/components/smart-grid";
import { IPaginationResultFrequencies } from "app/common/interfaces";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { FormSectionComponent } from "app/shared/components/form-ui/form-section/form-section.component";
import { ToggleOptionComponent } from "app/shared/components/form-ui/toggle-option/toggle-option.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { SubRouteQuotaService } from "./sub-route-quota.service";
import { CustomerCellRendererComponent } from "./cell-renderers/customer-cell-renderer.component";
import { GroupCellRendererComponent } from "./cell-renderers/group-cell-renderer.component";
import { SubGroupCellRendererComponent } from "./cell-renderers/sub-group-cell-renderer.component";
import { CurrencyCellRendererComponent } from "./cell-renderers/currency-cell-renderer.component";
import { DateCellRendererComponent } from "./cell-renderers/date-cell-renderer.component";
import { RouteCellRendererComponent } from "./cell-renderers/route-cell-renderer.component";
import { RoutesFiltersComponent } from "../../filters/routes-filters/routes-filters.component";
import { DateHelper } from "app/utils/date.helper";
import { DatesFiltersComponent } from "../../filters/dates-filters/dates-filters.component";

@Component({
  selector: "app-sub-route-quota",
  templateUrl: "./sub-route-quota.component.html",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    MatRowDef,
    MatSidenav,
    MatSidenavContainer,
    StatusFiltersComponent,
    HoverMenuDirective,
    SmartGridComponent,
    FormSectionComponent,
    ReactiveFormsModule,
    MatSelectModule,
    MatInputModule,
    ToggleOptionComponent,
    FilterComponent,
    DatesFiltersComponent,
    RoutesFiltersComponent
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
})
export class QuotaComponent implements AfterViewInit, OnDestroy {
  StatusEnum = StatusEnum

  dataSource = new MatTableDataSource<QuotaAttributes>([]);

  searchQuery: string = "";
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof QuotaAttributes = "businessPartnerCode";
  sortDirection: "ASC" | "DESC" = "DESC";

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;

  //#region Smart Grid
  // Using `any` because doesn't support QuotaAttributes.
  columnDefs: ColDef<any>[] = [ //QuotaAttributes
    {
      field: 'subRoutes',
      headerName: 'Rutas',
      cellRenderer: RouteCellRendererComponent,
      autoSize: true,
      minWidth: 150,
      sortable: true,
      filterOptions:{
        valueExtractor(item) {
          return item.subRoutesNames?.[0] || '';
        },
        customFilterFn(rowData, filterValue) {
          const name = rowData.subRoutesNames?.[0] || '';
          return name.toLowerCase().includes(filterValue.toLowerCase());
        }
      }
    },
    {
      field: 'businessPartnerCode',
      headerName: 'Cliente',
      cellRenderer: CustomerCellRendererComponent,
      autoSize: true,
      minWidth: 220,
      sortable: true,
    },
    {
      field: 'materialGroupCode',
      headerName: 'Categoría',
      cellRenderer: GroupCellRendererComponent,
      autoSize: true,
      minWidth: 220,
      sortable: true,
    },
    {
      field: 'materialSubGroupCode',
      headerName: 'SubCategoría',
      cellRenderer: SubGroupCellRendererComponent,
      autoSize: true,
      minWidth: 220,
      sortable: true,
    },
    {
      field: 'quota',
      headerName: 'Cuota',
      cellRenderer: CurrencyCellRendererComponent,
      autoSize: true,
      minWidth: 200,
      sortable: true,
    },
    {
      field: '_date',
      headerName: 'Fecha',
      cellRenderer: DateCellRendererComponent,
      autoSize: true,
      minWidth: 220,
      sortable: true,
      filterOptions: {
        valueExtractor(item) {
          if (!item._date) return '';

          const date = new Date(item._date);
          return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        },

        customFilterFn(rowData, filterValue) {
          const rowDate = rowData._date;
          if (!rowDate) return !filterValue;

          const rowDateObj = new Date(rowDate);
          const rowDateStr = rowDateObj.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });

          return rowDateStr === filterValue;
        },
      }
    },
  ]

  gridConfig: GridConfig<any> = { //QuotaAttributes
    emptyState: {
      message: 'No encontramos tus resultados!',
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: 'api',
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: 'api',
      multiColumn: false,
      defaultSort: [{ field: this.sortActive, direction: this.sortDirection == 'ASC' ? 'asc' : 'desc' }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: 'local'
    },
    columnVisibility: {
      enabled: false,
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40,
    },
    styling: {
      maxHeight: '85vh',
      density: 'comfortable',
      fontSize: '12px',
      cellPaddingX: '12px',
      cellPaddingY: '12px',
    },
    settingsPanel: {
      enabled: true,
      position: 'right',
      enableColumnVisibility: true,
      enableDensity: false,
      enableExport: true,
      defaultDensity: 'comfortable',
    },
  };
  //#endregion

  //BUSCADOR
  searchSubscription: Subscription;

  form: FormGroup;
  
  // Status Filter Properties
  loadFiltersData = false;
  loadRoutesData = false;
  selectedStatuses: number[] = [StatusEnum.ACTIVE, StatusEnum.INACTIVE]; // Default to show both active and inactive

  //#region Exportar
  fileToUpload: File;
  aux_file = false
  //#endregion

  constructor(
    private readonly subRouteQuotaService: SubRouteQuotaService,
    private readonly searchService: SearchService,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      subRouteId: [],
    });
  }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.findAllWithCount();
  }

  findAllWithCount(): void {
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.searchQuery,
      sort: this.sortActive,
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate: DateHelper.getEndOfDay(this.form.get("end")?.value) ?? DateHelper.getEndOfDay(this.form.get("start")?.value) ?? null,
      subRouteId: this.form.get("subRouteId")?.value,
    };

    this.subRouteQuotaService
      .findAllWithCount(options, filter)
      .subscribe({
        next: (res: IPaginationResultFrequencies<QuotaAttributes>) => {
          this.dataSource.data = res?.data?.data ?? [];
          this.totalItems = res?.data?.pageInfo?.[0]?.count ?? 0;
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener cuotas",
            "Cerrar",
            { duration: 5000 }
          );
        },
      })
  }

  changeSort(event: SortChangedEvent<any>) { //QuotaAttributes
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as any;
      this.sortDirection = sortState.direction.toUpperCase() as 'ASC' | 'DESC';
    } else {
      this.sortActive = 'businessPartnerCode';
      this.sortDirection = 'DESC';
    }
    this.findAllWithCount();
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.findAllWithCount();
  }

  onGridReady(event: GridReadyEvent<QuotaAttributes>): void {
    setTimeout(() => {
      event.api.autoSizeAllColumns();
    }, 100);
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.pageIndex = 1;
          this.findAllWithCount();
        }
      });
  }

  downloadQuotaTemplate() {
    const url = this.subRouteQuotaService.getFileTemplateQuota();
    if (!url) {
      this.snackBar.open("Plantilla no disponible.", "Cerrar", {
        duration: 3000,
      });
      return;
    }
    window.open(url);
  }

  async handleFileInput(event) {
    this.aux_file = true
    this.fileToUpload = event.target.files[0];
    if (this.fileToUpload) {
      if (this.fileToUpload.type != 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        this.snackBar.open("Ingrese solo de tipo .xlsx", "Cerrar", { duration: 3000 })
      }
    } else {
      this.aux_file = false
    }
  }

  onMessageConfirm() {
    if (this.fileToUpload) {

      let send = {
        file: this.fileToUpload
      }

      //formdata
      let formdata = new FormData()
      for (let key in send) {
        formdata.append(key, send[key])
      }

      this.subRouteQuotaService.importFile(formdata).subscribe({
        next: (res: any) => {
          this.snackBar.open(res.message, "Cerrar", { duration: 3000 })
          this.aux_file = false
          this.findAllWithCount()
        },
        error: (err) => {
          this.aux_file = false
          this.snackBar.open(err.error.message || 'Error al subir el registro', 'Cerrar', { duration: 3000 });
        },
      })
    }
  }

  // Status Filter Methods
  onRoutesExpanded(): void {
    this.loadRoutesData = true;
  }

  onClearFilter() {
    this.form.reset({
      start: new Date(),
      end: new Date(),
      subRouteId: null,
    });
    this.findAllWithCount();
  }

  applyStatusFilter(): void {
    this.pageIndex = 1;
    this.findAllWithCount();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}